import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';
import { riderAPI } from '../../services/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const RiderNotifications: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'jobs' | 'payments' | 'messages'>('all');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await riderAPI.getNotifications(50);
      setNotifications(data.notifications);
    } catch (err: any) {
      setError(err.message || 'Failed to load notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'job':
        return <TruckIcon className="h-5 w-5 text-volexx-blue-600" />;
      case 'payment':
        return <CurrencyDollarIcon className="h-5 w-5 text-green-600" />;
      case 'message':
        return <ChatBubbleLeftIcon className="h-5 w-5 text-volexx-orange-600" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatTime = (isoTime: string) => {
    const date = new Date(isoTime);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications.filter(n => {
        if (filter === 'jobs') return n.type === 'job';
        if (filter === 'payments') return n.type === 'payment';
        if (filter === 'messages') return n.type === 'message';
        return true;
      });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-volexx-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Notifications
          </h1>
          <p className="mt-2 text-gray-600">Stay updated with your delivery activities</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-volexx p-4">
            <p className="text-red-800 text-sm">{error}</p>
            <button
              onClick={fetchNotifications}
              className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="mb-6 flex space-x-2 overflow-x-auto">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-volexx font-medium text-sm transition-colors whitespace-nowrap ${
              filter === 'all'
                ? 'bg-volexx-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('jobs')}
            className={`px-4 py-2 rounded-volexx font-medium text-sm transition-colors whitespace-nowrap ${
              filter === 'jobs'
                ? 'bg-volexx-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Jobs
          </button>
          <button
            onClick={() => setFilter('payments')}
            className={`px-4 py-2 rounded-volexx font-medium text-sm transition-colors whitespace-nowrap ${
              filter === 'payments'
                ? 'bg-volexx-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Payments
          </button>
          <button
            onClick={() => setFilter('messages')}
            className={`px-4 py-2 rounded-volexx font-medium text-sm transition-colors whitespace-nowrap ${
              filter === 'messages'
                ? 'bg-volexx-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Messages
          </button>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
          {filteredNotifications.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <BellIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No notifications</p>
              <p className="text-sm text-gray-400 mt-1">
                {filter === 'all' 
                  ? "You're all caught up!" 
                  : `No ${filter} notifications`}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                    !notification.read ? 'bg-volexx-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-2 rounded-volexx ${
                      notification.type === 'job' ? 'bg-volexx-blue-100' :
                      notification.type === 'payment' ? 'bg-green-100' :
                      notification.type === 'message' ? 'bg-volexx-orange-100' :
                      'bg-gray-100'
                    }`}>
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            {formatTime(notification.time)}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="ml-4">
                            <div className="h-2 w-2 bg-volexx-blue-600 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notification Settings */}
        <div className="mt-6 bg-gray-50 rounded-volexx p-4">
          <p className="text-sm text-gray-600">
            Manage your notification preferences in <a href="/rider-profile" className="text-volexx-blue-600 hover:text-volexx-blue-700 font-medium">Profile Settings</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RiderNotifications;
